/**
 * Alerta sonoro para briefings pendentes da secretária.
 *
 * Componente headless (sem UI) renderizado no Shell. Mantém um listener
 * em tempo real dos `secretaryBriefing.briefed=false` e toca um par de
 * beeps a cada 30 segundos enquanto houver pelo menos um briefing
 * pendente. Para quando a secretária marca todos como "Paciente
 * orientado".
 *
 * Som gerado via Web Audio API (sem arquivo .mp3) — duplo beep curto em
 * 880Hz com envelope attack/decay pra não estalar.
 *
 * Restrição de autoplay: browsers bloqueiam áudio antes da primeira
 * interação do usuário. Como a secretária já clicou pra logar antes do
 * Shell renderizar, o gesture já foi capturado e o som funciona. Se o
 * AudioContext estiver suspenso, tentamos `resume()` antes de tocar.
 *
 * Só ativo para roles que veem briefings (mesma regra do hook
 * `usePendencias` / Topbar). Outros papéis nunca ouvem som.
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { canSee } from '@/hooks/usePendencias';
import { SecretaryBriefingService } from '@/services/firebase/secretaryBriefing';
import {
  SecretaryAudioAlertService,
  DEFAULT_SECRETARY_AUDIO_ALERT,
} from '@/services/firebase/secretaryAudioAlert';

/** Frequência do beep — A5 (880Hz). Audível mas não estridente. */
const BEEP_FREQ_HZ = 880;
/** Duração de cada beep individual. */
const BEEP_DURATION_MS = 150;
/** Pausa entre os dois beeps do par. */
const BEEP_GAP_MS = 100;
/** Volume do beep (0..1). 0.15 = audível em escritório sem assustar. */
const BEEP_VOLUME = 0.15;

/**
 * Toca um único beep com envelope curto pra evitar click no início/fim.
 * Retorna uma Promise que resolve quando o beep termina.
 */
function playBeep(ctx: AudioContext, startTimeOffsetSec = 0): Promise<void> {
  return new Promise((resolve) => {
    const now = ctx.currentTime + startTimeOffsetSec;
    const durationSec = BEEP_DURATION_MS / 1000;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(BEEP_FREQ_HZ, now);

    const gain = ctx.createGain();
    // Envelope: attack 10ms → sustain → decay nos últimos 30ms.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(BEEP_VOLUME, now + 0.01);
    gain.gain.setValueAtTime(BEEP_VOLUME, now + durationSec - 0.03);
    gain.gain.linearRampToValueAtTime(0, now + durationSec);

    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + durationSec);
    osc.onended = () => resolve();
  });
}

/** Toca dois beeps em sequência (ding-ding). */
async function playDoubleBeep(ctx: AudioContext): Promise<void> {
  // Se o ctx estiver suspenso (autoplay policy), tenta retomar. Ignora
  // erro silenciosamente — usuário pode interagir depois e funcionará.
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return;
    }
  }
  // Agenda os dois beeps no relógio do AudioContext em sequência —
  // gap entre eles fica preciso sem depender de setTimeout.
  await playBeep(ctx, 0);
  await playBeep(ctx, (BEEP_DURATION_MS + BEEP_GAP_MS) / 1000);
}

export const BriefingAlertSound = () => {
  const { clinicId, role } = useAuth();
  const audioCtxRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countRef = useRef(0);

  // Intervalo entre repetições — carregado da config da clínica
  // (`clinics/{cId}/settings/secretaryAudioAlert`). Default 30s até
  // carregar; recria o interval ativo se o valor mudar.
  const [intervalSeconds, setIntervalSeconds] = useState<number>(
    DEFAULT_SECRETARY_AUDIO_ALERT.alertIntervalSeconds,
  );

  // Inicializa AudioContext lazy — só cria quando há permissão de role,
  // pra não consumir recurso à toa pra médico/admin que não ouvem.
  useEffect(() => {
    if (!canSee(role, 'BRIEFING_NEEDED')) return;
    if (audioCtxRef.current) return;
    try {
      // Type narrowing pro AudioContext do Safari (webkitAudioContext).
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      audioCtxRef.current = new Ctor();
    } catch (err) {
      console.warn('[briefing-alert] AudioContext init failed:', err);
    }
    return () => {
      // Não fecha o context no unmount — reusar é mais barato que recriar
      // se o componente remontar (ex.: hot reload). Browser limpa no
      // unload da página.
    };
  }, [role]);

  // Carrega a config da clínica. One-shot — admin que muda o valor
  // precisa pedir refresh nas abas das secretárias pra propagar
  // (mensagem na tab informa isso ao admin).
  useEffect(() => {
    if (!clinicId || !canSee(role, 'BRIEFING_NEEDED')) return;
    let cancelled = false;
    void (async () => {
      try {
        const cfg = await SecretaryAudioAlertService.load(clinicId);
        if (!cancelled) setIntervalSeconds(cfg.alertIntervalSeconds);
      } catch (err) {
        console.warn('[briefing-alert] config load failed, usando default:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicId, role]);

  // Listener em tempo real dos briefings pendentes. Gerencia o interval
  // que toca o som — inicia quando count vai pra 1+, para quando volta a 0.
  // Recria o interval se `intervalSeconds` mudar enquanto está ativo.
  useEffect(() => {
    if (!clinicId || !canSee(role, 'BRIEFING_NEEDED')) return;

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const playNow = () => {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      void playDoubleBeep(ctx).catch((err) => {
        console.warn('[briefing-alert] play failed:', err);
      });
    };

    const startInterval = () => {
      stopInterval();
      intervalRef.current = setInterval(() => {
        if (countRef.current > 0) playNow();
      }, intervalSeconds * 1000);
    };

    // Se a config mudou enquanto havia pendências, reinicia o interval
    // com o novo valor (sem disparar beep extra — o timing reseta).
    if (countRef.current > 0) startInterval();

    const unsub = SecretaryBriefingService.subscribePendingBriefings(
      clinicId,
      (items) => {
        const prevCount = countRef.current;
        const nextCount = items.length;
        countRef.current = nextCount;

        if (nextCount === 0) {
          // Todos orientados — para o som.
          stopInterval();
          return;
        }

        if (prevCount === 0 && nextCount > 0) {
          // Primeira pendência aparecendo: toca imediatamente e
          // agenda repetições no intervalo configurado.
          playNow();
          startInterval();
        }
        // Caso prevCount > 0 && nextCount > 0: interval já está rodando,
        // nada a fazer. Mudança no número de pendências não dispara beep
        // extra — só o tick periódico do interval.
      },
    );

    return () => {
      unsub();
      stopInterval();
    };
  }, [clinicId, role, intervalSeconds]);

  return null;
};
